/** Shared photo/video picker state + validation for every post composer
 * (feed, map group, game group, profile). A post carries up to 4 photos OR
 * one video, with client-side size/type checks mirroring the backend's own
 * limits (social/serializers.py SocialPostSerializer). */
export interface AttachmentPreview {
  file: File;
  type: 'image' | 'video';
  url: string;
}

export class PostAttachmentPicker {
  static readonly MAX_IMAGE_MB = 10;
  static readonly MAX_VIDEO_MB = 50;
  static readonly MAX_IMAGES = 4;

  previews: AttachmentPreview[] = [];
  error = '';

  /** The first attached file — what "is anything attached?" checks read. */
  get file(): File | null {
    return this.previews[0]?.file ?? null;
  }

  get type(): 'image' | 'video' | null {
    return this.previews[0]?.type ?? null;
  }

  get images(): File[] {
    return this.previews.filter((p) => p.type === 'image').map((p) => p.file);
  }

  get video(): File | null {
    return this.previews.find((p) => p.type === 'video')?.file ?? null;
  }

  get canAddMore(): boolean {
    return this.type !== 'video' && this.previews.length < PostAttachmentPicker.MAX_IMAGES;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []);
    input.value = ''; // allow re-selecting the same file later
    if (!picked.length) {
      return;
    }
    this.error = '';

    for (const file of picked) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        this.error = 'Attach photos or a video.';
        continue;
      }
      const maxMb = isVideo ? PostAttachmentPicker.MAX_VIDEO_MB : PostAttachmentPicker.MAX_IMAGE_MB;
      if (file.size > maxMb * 1024 * 1024) {
        this.error = `${isVideo ? 'Video' : 'Each photo'} must be smaller than ${maxMb}MB.`;
        continue;
      }

      if (isVideo) {
        // A video stands alone: it replaces whatever was attached.
        this.clearPreviews();
        this.previews = [{ file, type: 'video', url: URL.createObjectURL(file) }];
        break;
      }
      if (this.type === 'video') {
        this.clearPreviews(); // photos replace a video
      }
      if (this.previews.length >= PostAttachmentPicker.MAX_IMAGES) {
        this.error = `You can attach up to ${PostAttachmentPicker.MAX_IMAGES} photos.`;
        break;
      }
      this.previews = [...this.previews, { file, type: 'image', url: URL.createObjectURL(file) }];
    }
  }

  removeAt(index: number): void {
    const removed = this.previews[index];
    if (!removed) {
      return;
    }
    URL.revokeObjectURL(removed.url);
    this.previews = this.previews.filter((_, i) => i !== index);
    this.error = '';
  }

  clear(): void {
    this.clearPreviews();
    this.error = '';
  }

  private clearPreviews(): void {
    this.previews.forEach((p) => URL.revokeObjectURL(p.url));
    this.previews = [];
  }
}
